import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useMapsLibrary, Map as GoogleMap, Marker, useMap } from "@vis.gl/react-google-maps";
import { CheckCircle, Warning, MapTrifold, MapPin, CaretDown, CaretUp } from "@phosphor-icons/react";
import { useGoogleMapsStatus } from "./GoogleMapsProvider";

/**
 * Forces an internal resize/recenter cycle on the Google Map after it
 * mounts inside a modal/expandable container. Without this, maps that
 * mount inside `display:none`/hidden parents render with 0×0 tiles.
 */
function MapResizeFix({ center }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const t1 = setTimeout(() => {
      try {
        window.google?.maps?.event?.trigger(map, "resize");
        if (center) map.panTo(center);
      } catch { /* noop */ }
    }, 60);
    const t2 = setTimeout(() => {
      try {
        window.google?.maps?.event?.trigger(map, "resize");
        if (center) map.panTo(center);
      } catch { /* noop */ }
    }, 400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [map, center?.lat, center?.lng]);
  return null;
}

/**
 * Global Address Autocomplete + Map Verification.
 *
 * Primary: Google Places API (New) — AutocompleteSuggestion w/ session tokens for billing efficiency.
 * Fallback: Nominatim (free OSM) via /api/geocode/search proxy on the backend.
 *
 * Emits structured data via onPlace():
 *   {
 *     address (formatted), street, city, state, zip,
 *     lat, lng,
 *     verified: bool, verified_at: ISO string | null,
 *     source: "google" | "nominatim" | "manual"
 *   }
 *
 * Props:
 *   value, onChange(text), onPlace(payload),
 *   lat, lng, verified, verifiedAt — initial values (component is controlled-ish)
 *   placeholder, className, testid, required, disabled
 *   showMap (default true), defaultMapOpen (null = open on desktop, closed on mobile)
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onPlace,
  lat,
  lng,
  verified,
  verifiedAt,
  placeholder = "Start typing an address…",
  className = "zmex-input",
  testid = "address-autocomplete",
  required = false,
  disabled = false,
  showMap = true,
  defaultMapOpen = null,
}) {
  const places = useMapsLibrary("places");
  const mapsStatus = useGoogleMapsStatus();
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const cacheRef = useRef(new Map());
  const fallbackOkRef = useRef(true);

  const [text, setText] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [pos, setPos] = useState(typeof lat === "number" && typeof lng === "number" ? { lat, lng } : null);
  const [isVerified, setIsVerified] = useState(!!verified);
  const [verifiedTs, setVerifiedTs] = useState(verifiedAt || null);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState("idle");
  const [error, setError] = useState("");

  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  }, []);
  const [mapOpen, setMapOpen] = useState(defaultMapOpen ?? !isMobile);

  // Sync external value resets
  useEffect(() => {
    setText(value || "");
  }, [value]);
  useEffect(() => {
    if (typeof lat === "number" && typeof lng === "number") {
      setPos({ lat, lng });
    } else if (lat == null || lng == null) {
      setPos(null);
    }
  }, [lat, lng]);
  useEffect(() => { setIsVerified(!!verified); }, [verified]);
  useEffect(() => { setVerifiedTs(verifiedAt || null); }, [verifiedAt]);

  // Close dropdown on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Refresh session token after a selection (Google billing optimization)
  function refreshSessionToken() {
    if (places?.AutocompleteSessionToken) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
  }
  useEffect(() => { refreshSessionToken(); }, [places]);

  // ---------- Suggestion fetchers ----------
  const fetchGoogle = useCallback(async (input) => {
    if (!places?.AutocompleteSuggestion) throw new Error("Places API New unavailable");
    const req = {
      input,
      sessionToken: sessionTokenRef.current,
      includedRegionCodes: ["us"],
      // Florida bounds (LatLngBoundsLiteral — the JS SDK rejects the REST "rectangle" shape)
      locationBias: { south: 24.396308, west: -87.634896, north: 31.000888, east: -79.974307 },
    };
    const { suggestions: out } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(req);
    return (out || []).map((s) => {
      const pp = s.placePrediction;
      return {
        kind: "google",
        prediction: pp,
        place_id: pp?.placeId,
        primary: pp?.mainText?.text || pp?.text?.text || "",
        secondary: pp?.secondaryText?.text || "",
        full: pp?.text?.text || "",
      };
    }).filter((x) => x.full);
  }, [places]);

  const fetchNominatim = useCallback(async (input) => {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(input)}&format=jsonv2&addressdetails=1&limit=6&countrycodes=us`;
    const r = await fetch(url, { headers: { "Accept": "application/json" } });
    if (!r.ok) throw new Error(`Nominatim HTTP ${r.status}`);
    const arr = await r.json();
    return (arr || []).map((it) => ({
      kind: "nominatim",
      primary: (it.display_name || "").split(",")[0] || it.display_name || "",
      secondary: (it.display_name || "").split(",").slice(1).join(",").trim(),
      full: it.display_name || "",
      lat: parseFloat(it.lat),
      lng: parseFloat(it.lon),
      raw: it,
    }));
  }, []);

  const queryFor = useCallback(async (input) => {
    const cached = cacheRef.current.get(input);
    if (cached) return cached;
    let results = [];
    if (fallbackOkRef.current && places) {
      try {
        results = await fetchGoogle(input);
        setProvider("google");
        setError("");
      } catch (e) {
        console.warn("Google autocomplete failed, falling back to OpenStreetMap:", e);
        // Google failed — try Nominatim
        try {
          results = await fetchNominatim(input);
          setProvider("nominatim");
          setError("");
        } catch (e2) {
          setError("Autocomplete unavailable — you can still type manually.");
          results = [];
        }
      }
    } else {
      try {
        results = await fetchNominatim(input);
        setProvider("nominatim");
      } catch (e2) {
        setError("Autocomplete unavailable — you can still type manually.");
        results = [];
      }
    }
    cacheRef.current.set(input, results);
    // Keep cache bounded
    if (cacheRef.current.size > 60) {
      const firstKey = cacheRef.current.keys().next().value;
      cacheRef.current.delete(firstKey);
    }
    return results;
  }, [fetchGoogle, fetchNominatim, places]);

  // Debounced input handler
  function handleInput(v) {
    setText(v);
    onChange?.(v);
    setIsVerified(false);
    setVerifiedTs(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v || v.trim().length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const out = await queryFor(v.trim());
      setSuggestions(out);
      setShowDropdown(out.length > 0);
      setHighlighted(out.length ? 0 : -1);
      setLoading(false);
    }, 350);
  }

  // ---------- Place-component parsing ----------
  function parseGoogleAddressComponents(components) {
    // Components may be googleplaces v1 (shortText/longText/types) or v3 (short_name/long_name/types)
    const get = (typesWanted) => {
      const found = components?.find((c) => (c.types || []).some((t) => typesWanted.includes(t)));
      if (!found) return "";
      return found.shortText ?? found.short_name ?? found.longText ?? found.long_name ?? "";
    };
    const streetNumber = get(["street_number"]);
    const route = get(["route"]);
    const street = [streetNumber, route].filter(Boolean).join(" ").trim();
    const city = get(["locality"]) || get(["sublocality_level_1"]) || get(["sublocality"]) || get(["administrative_area_level_2"]);
    const state = get(["administrative_area_level_1"]);
    const zip = get(["postal_code"]);
    return { street, city, state, zip };
  }

  function parseNominatimAddress(addr) {
    if (!addr) return { street: "", city: "", state: "", zip: "" };
    const street = [addr.house_number, addr.road].filter(Boolean).join(" ").trim();
    const city = addr.city || addr.town || addr.village || addr.hamlet || addr.suburb || "";
    const state = addr.state || "";
    const zip = addr.postcode || "";
    return { street, city, state, zip };
  }

  async function selectSuggestion(s) {
    setShowDropdown(false);
    setLoading(true);
    try {
      let address = s.full;
      let street = "", city = "", state = "", zip = "";
      let latv = null, lngv = null;
      let source = s.kind;

      if (s.kind === "google" && s.prediction) {
        const place = s.prediction.toPlace();
        await place.fetchFields({ fields: ["formattedAddress", "addressComponents", "location", "displayName"] });
        address = place.formattedAddress || place.displayName || s.full;
        latv = place.location?.lat?.() ?? place.location?.lat ?? null;
        lngv = place.location?.lng?.() ?? place.location?.lng ?? null;
        const parts = parseGoogleAddressComponents(place.addressComponents || []);
        street = parts.street; city = parts.city; state = parts.state; zip = parts.zip;
        refreshSessionToken();
      } else if (s.kind === "nominatim") {
        latv = s.lat; lngv = s.lng;
        const parts = parseNominatimAddress(s.raw?.address || {});
        street = parts.street; city = parts.city; state = parts.state; zip = parts.zip;
      }

      setText(address);
      setPos(latv != null && lngv != null ? { lat: latv, lng: lngv } : null);
      onChange?.(address);

      const payload = {
        address, street, city, state, zip,
        lat: latv, lng: lngv,
        verified: false, // not verified until user confirms on map
        verified_at: null,
        source,
      };
      onPlace?.(payload);
    } catch (e) {
      setError("Couldn't load address details — try another suggestion.");
    } finally {
      setLoading(false);
    }
  }

  function verifyNow() {
    if (!pos) return;
    const ts = new Date().toISOString();
    setIsVerified(true);
    setVerifiedTs(ts);
    onPlace?.({
      address: text,
      street: "", city: "", state: "", zip: "", // unchanged; parent merges
      lat: pos.lat, lng: pos.lng,
      verified: true,
      verified_at: ts,
      source: provider,
      __partial: true, // hint to parent: only update verified status + coords
    });
  }

  function onMarkerDrag(e) {
    let latv, lngv;
    if (e.latLng && typeof e.latLng.lat === "function") {
      latv = e.latLng.lat();
      lngv = e.latLng.lng();
    } else if (typeof e.latLng?.lat === "number") {
      latv = e.latLng.lat;
      lngv = e.latLng.lng;
    } else {
      return;
    }
    setPos({ lat: latv, lng: lngv });
    setIsVerified(false);
    setVerifiedTs(null);
    onPlace?.({
      address: text,
      street: "", city: "", state: "", zip: "",
      lat: latv, lng: lngv,
      verified: false, verified_at: null,
      source: "manual",
      __partial: true,
    });
  }

  function onKeyDown(e) {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      if (highlighted >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[highlighted]);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  }

  return (
    <div className="relative w-full" ref={wrapRef}>
      {/* Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => suggestions.length && setShowDropdown(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          data-testid={testid}
          className={className}
          autoComplete="off"
          spellCheck="false"
        />
        {loading ? (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white/45 uppercase tracking-[0.15em]">…</div>
        ) : null}
      </div>

      {/* Dropdown */}
      {showDropdown && suggestions.length > 0 ? (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 bg-[#0a1422] border border-white/15 rounded-sm shadow-2xl max-h-72 overflow-y-auto"
          data-testid={`${testid}-dropdown`}
        >
          {suggestions.map((s, i) => (
            <li
              key={(s.place_id || `${s.lat},${s.lng}`) + i}
              onMouseEnter={() => setHighlighted(i)}
              onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
              className={`px-3 py-2 cursor-pointer border-b border-white/5 last:border-b-0 ${highlighted === i ? "bg-[#CFAB7A]/15" : "hover:bg-white/5"}`}
              data-testid={`${testid}-option-${i}`}
            >
              <div className="flex items-start gap-2">
                <MapPin size={12} weight="bold" className="text-[#CFAB7A] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{s.primary}</div>
                  {s.secondary ? <div className="text-[11px] text-white/50 truncate">{s.secondary}</div> : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Verification status row */}
      {pos || isVerified || error ? (
        <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-[11px]">
            {isVerified ? (
              <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold" data-testid={`${testid}-verified-badge`}>
                <CheckCircle size={11} weight="fill" /> Verified
                {verifiedTs ? <span className="text-[#5C738A]">· {new Date(verifiedTs).toLocaleDateString()}</span> : null}
              </span>
            ) : pos ? (
              <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                <Warning size={11} weight="fill" /> Address selected — verify on map below
              </span>
            ) : null}
            {provider !== "idle" && !error ? (
              <span className="text-[10px] text-[#5C738A] uppercase tracking-[0.15em]" data-testid={`${testid}-provider`}>via {provider}</span>
            ) : null}
            {error ? (
              <span className="text-rose-600 inline-flex items-center gap-1">
                <Warning size={11} weight="fill" /> {error}
              </span>
            ) : null}
          </div>

          {showMap && pos ? (
            <button
              type="button"
              onClick={() => setMapOpen((v) => !v)}
              className="text-[10px] uppercase tracking-[0.18em] text-[#012340] hover:text-[#CFAB7A] font-bold inline-flex items-center gap-1"
              data-testid={`${testid}-map-toggle`}
            >
              <MapTrifold size={11} weight="bold" />
              {mapOpen ? "Hide map" : "Verify on map"}
              {mapOpen ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Verification map */}
      {showMap && pos && mapOpen ? (
        <div className="mt-2 border border-white/10 rounded-sm overflow-hidden" data-testid={`${testid}-map`}>
          <div className="h-56 w-full">
            {mapsStatus.apiKeyMissing ? (
              <div className="w-full h-full grid place-items-center text-center px-4 py-3 text-xs bg-[#0a1422]" data-testid={`${testid}-map-fallback`}>
                <div>
                  <div className="text-rose-300 font-semibold mb-1">Map failed to load.</div>
                  <div className="text-white/55">Please check Google Maps API configuration.</div>
                  <div className="text-white/45 mt-1 font-mono text-[11px]">{pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</div>
                </div>
              </div>
            ) : mapsStatus.authFailed ? (
              <div className="w-full h-full grid place-items-center text-center px-4 py-3 text-xs bg-[#0a1422]" data-testid={`${testid}-map-fallback`}>
                <div>
                  <div className="text-rose-300 font-semibold mb-1">Map failed to load.</div>
                  <div className="text-white/55">Please check Google Maps API configuration (HTTP referrer restrictions, billing, or enabled APIs).</div>
                  <div className="text-white/45 mt-1 font-mono text-[11px]">Pin: {pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</div>
                  <div className="text-white/40 mt-1">You can still type lat/lng manually or use the address autocomplete above.</div>
                </div>
              </div>
            ) : places ? (
              <GoogleMap
                style={{ width: "100%", height: "100%" }}
                defaultCenter={pos}
                center={pos}
                defaultZoom={17}
                gestureHandling="cooperative"
                disableDefaultUI={false}
                mapTypeControl={false}
                streetViewControl={false}
                fullscreenControl={false}
              >
                <MapResizeFix center={pos} />
                <Marker position={pos} draggable onDragEnd={onMarkerDrag} />
              </GoogleMap>
            ) : (
              <div className="w-full h-full grid place-items-center text-white/45 text-xs bg-[#0a1422]">
                Loading map… {pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}
              </div>
            )}
          </div>
          <div className="bg-[#0a1422] border-t border-white/10 px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] text-white/55">
              <span className="text-white/40 uppercase tracking-[0.15em] text-[10px] mr-1">Pin:</span>
              {pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}
              <span className="text-white/30 ml-2">· Drag the pin to fine-tune</span>
            </div>
            {!isVerified ? (
              <button
                type="button"
                onClick={verifyNow}
                className="zmex-btn-primary text-[10px] uppercase tracking-[0.18em] py-1.5 px-2.5"
                data-testid={`${testid}-verify-btn`}
              >
                <CheckCircle size={11} weight="bold" /> Verify Address
              </button>
            ) : (
              <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px] uppercase tracking-[0.18em]">
                <CheckCircle size={11} weight="fill" /> Address Verified
              </span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
